import { combineLatest, forkJoin, iif as observableIif, of, Subject } from 'rxjs';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { SubcontractorService } from '../shared/subcontractor.service';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { Subcontractor } from '../shared/subcontractor.model';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService } from '../../shared/services/navigation.service';
import { Carrier } from '../shared/carrier.model';
import { CarrierService } from '../shared/carrier.service';
import { finalize } from 'rxjs/operators';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { NameSearchCriteria } from '../shared/name-search-criteria.model';
import { DataTableColumn } from '../../shared/data-table/data-table.component';
import { SearchObjectResult } from '../../shared/search-object-result.model';

@Component({
  selector: 'app-subcontractor-list',
  templateUrl: './subcontractor-list.component.html',
  styleUrls: ['./subcontractor-list.component.scss']
})
export class SubcontractorListComponent implements OnInit {

  carrierId: string;
  selectedCarrier: Carrier;

  carriers: Carrier[]             = [];

  selfCarrierMode = false;

  filters: NameSearchCriteria = {};
  columns: DataTableColumn[] = [];
  loadingSubject: Subject<number> = new Subject<number>();

  @ViewChild('nameColumn', { static: true })
  nameColumn: TemplateRef<any>;

  @ViewChild('vatCodeColumn', { static: true })
  vatCodeColumn: TemplateRef<any>;

  @ViewChild('loadGroupColumn', { static: true })
  loadGroupColumn: TemplateRef<any>;

  @ViewChild('blujayCredentialsColumn', { static: true })
  blujayCredentialsColumn: TemplateRef<any>;

  @ViewChild('actionsColumn', { static: true })
  actionsColumn: TemplateRef<any>;

  loadPage = (page: number, pageSize: number) => this.subcontractorService.search(page - 1, pageSize, this.filters, this.selfCarrierMode, this.carrierId);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private navigation: NavigationService,
    private carrierService: CarrierService,
    private subcontractorService: SubcontractorService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
  ) {
    combineLatest([this.route.params, this.route.data])
      .subscribe(([params, data]) => {
        this.carrierId = params.carrierId;

        if (data.selfCarrierMode) {
          this.selfCarrierMode = true;
        }

        if (!this.selfCarrierMode && !this.carrierId) {
          toastr.error('Could not find the carrierId, redirecting to the carrier list', 'Error');
          this.navigation.carriers()
            .list()
            .go();
        }

        this.loader.startLoad(false);

        const carriersObservable = observableIif(
          () => this.selfCarrierMode,
          of([]),
          carrierService.getAll()
        );

        forkJoin([carriersObservable])
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(([carriers]) => {
            this.carriers       = carriers;

            this.selectedCarrier = carriers.find(c => c.id === this.carrierId);

            this.resetFilters();
            this.loadingSubject.next();
          }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.subcontractors.list.errors.fetch-subcontractors', false));
      });
  }

  ngOnInit() {
    this.columns = [
      {
        name: 'name',
        displayed: true,
        translationKey: 'user-dashboard.subcontractors.list.table.name',
        template: this.nameColumn,
        sortable: false,
      }, {
        name: 'vatCode',
        displayed: true,
        translationKey: 'user-dashboard.subcontractors.list.table.vat-code',
        template: this.vatCodeColumn,
        sortable: false,
      }, {
        name: 'loadGroup',
        displayed: true,
        translationKey: 'user-dashboard.subcontractors.list.table.load-group',
        template: this.loadGroupColumn,
        sortable: false,
      }, {
        name: 'blujayCredentials',
        displayed: true,
        translationKey: 'user-dashboard.subcontractors.list.table.blujay-credentials',
        template: this.blujayCredentialsColumn,
        sortable: false,
      }, {
        name: 'actions',
        displayed: true,
        translationKey: 'user-dashboard.subcontractors.list.table.actions',
        template: this.actionsColumn,
        sortable: false,
      },
    ];

    this.route.queryParams.subscribe(params => {
      this.filters.name = params.name;
    })
  }

  showBlujayCredentials(subcontractor: Subcontractor) {
    subcontractor.showBlujayCredentials = true;
  }

  goToSelfCarrierDrivers() {
    this.navigation.selfCarrier()
      .drivers()
      .list()
      .go();
  }

  goToSubcontractors(carrierId: string): void {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .list()
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(carrierId)
        .list()
        .go();
    }
  }

  goToCreateSubcontractor(): void {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .create()
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(this.carrierId)
        .create()
        .go();
    }
  }

  goToEditSubcontractor(subcontractor: Subcontractor): void {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .edit(subcontractor.id)
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(this.carrierId)
        .edit(subcontractor.id)
        .go();
    }
  }

  goToCreateDriver(subcontractor: Subcontractor): void {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .drivers(subcontractor.id)
        .create()
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(this.carrierId)
        .drivers(subcontractor.id)
        .create()
        .go();
    }
  }

  goToViewDrivers(subcontractor: Subcontractor): void {
    if (this.selfCarrierMode) {
      this.navigation.selfCarrier()
        .subcontractors()
        .drivers(subcontractor.id)
        .list()
        .go();
    } else {
      this.navigation.carriers()
        .subcontractors(this.carrierId)
        .drivers(subcontractor.id)
        .list()
        .go();
    }
  }

  pageLoaded(result: SearchObjectResult<Carrier>) {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {
          name: this.filters.name,
        }
      }
    );
  }

  resetFilters() {
    this.filters.name = '';
  }
}
